import { useState, useEffect, useCallback } from "react";
import { Flag, ChevronDown, ChevronUp, Loader2, AlertTriangle, Check, X, Pencil } from "lucide-react";
import { getQuestionFlags, editQuestion, dismissQuestionFlags } from "@/lib/api";

// ── Helpers ────────────────────────────────────────────────────────────────────

function Ts({ iso }) {
  if (!iso) return <span className="text-slate-400">—</span>;
  const d = new Date(iso);
  return (
    <span className="tabular-nums text-xs text-slate-500">
      {d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "2-digit" })}{" "}
      {d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
    </span>
  );
}

function CollectionBadge({ collection }) {
  const isFM = collection === "fm_questions";
  return (
    <span
      className="inline-block px-2 py-0.5 rounded text-xs font-bold"
      style={
        isFM
          ? { background: "#f5f3ff", color: "#7c3aed" }
          : { background: "#eff6ff", color: "#1d4ed8" }
      }
    >
      {isFM ? "FM" : "Subject"}
    </span>
  );
}

function ReasonBadge({ reason }) {
  return (
    <span className="inline-block px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs">
      {reason}
    </span>
  );
}

// Truncate question text at 80 chars
function Truncated({ text, max = 80 }) {
  if (!text) return <span className="text-slate-400 italic">—</span>;
  const short = text.length > max ? text.slice(0, max) + "…" : text;
  return (
    <span title={text} className="cursor-help">
      {short}
    </span>
  );
}

// ── Inline edit panel ──────────────────────────────────────────────────────────

function EditPanel({ row, token, onSaved, onCancel }) {
  const isFM = row.collection === "fm_questions";
  const [questionText, setQuestionText] = useState(row.question_text || "");
  const [optA, setOptA] = useState(row.options?.A || "");
  const [optB, setOptB] = useState(row.options?.B || "");
  const [optC, setOptC] = useState(row.options?.C || "");
  const [optD, setOptD] = useState(row.options?.D || "");
  const [correctAnswer, setCorrectAnswer] = useState(row.correct_answer || "");
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = { question_text: questionText, correct_answer: correctAnswer };
      if (!isFM) {
        payload.options = { A: optA, B: optB, C: optC, D: optD };
      }
      await editQuestion(row.record_id, row.collection, payload, token);
      setSavedMsg(true);
      setTimeout(() => {
        onSaved({ question_text: questionText, options: isFM ? null : payload.options, correct_answer: correctAnswer });
      }, 900);
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="px-6 py-5 border-t"
      style={{ background: "#f8fafc", borderColor: "#e2e8f0" }}
      data-testid={`edit-panel-${row.record_id}`}
    >
      <div className="max-w-2xl space-y-4">
        {/* Question text */}
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
            Question Text
          </label>
          <textarea
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
            data-testid={`edit-question-text-${row.record_id}`}
          />
        </div>

        {/* Options — Subject only */}
        {!isFM && (
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Options
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[["A", optA, setOptA], ["B", optB, setOptB], ["C", optC, setOptC], ["D", optD, setOptD]].map(
                ([letter, val, setter]) => (
                  <div key={letter} className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500 w-4 flex-shrink-0">{letter}</span>
                    <input
                      type="text"
                      value={val}
                      onChange={(e) => setter(e.target.value)}
                      className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      data-testid={`edit-option-${letter.toLowerCase()}-${row.record_id}`}
                    />
                  </div>
                )
              )}
            </div>
          </div>
        )}

        {/* Correct answer */}
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
            Correct Answer
          </label>
          {isFM ? (
            <input
              type="text"
              value={correctAnswer}
              onChange={(e) => setCorrectAnswer(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400 w-40"
              data-testid={`edit-correct-answer-${row.record_id}`}
            />
          ) : (
            <select
              value={correctAnswer}
              onChange={(e) => setCorrectAnswer(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
              data-testid={`edit-correct-answer-${row.record_id}`}
            >
              <option value="">— select —</option>
              {["A", "B", "C", "D"].map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          )}
        </div>

        {error && (
          <p className="text-xs text-red-600 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> {error}
          </p>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pt-1">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || savedMsg}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              savedMsg
                ? "bg-emerald-100 text-emerald-700"
                : "bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
            }`}
            data-testid={`edit-save-btn-${row.record_id}`}
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : savedMsg ? (
              <Check className="w-3.5 h-3.5" />
            ) : (
              <Check className="w-3.5 h-3.5" />
            )}
            {savedMsg ? "Saved" : saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-full text-sm font-semibold text-slate-500 hover:bg-slate-100 transition-colors"
            data-testid={`edit-cancel-btn-${row.record_id}`}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Table row ──────────────────────────────────────────────────────────────────

function FlagRow({ row, token, onDismiss, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const isFM = row.collection === "fm_questions";

  const handleSaved = (updates) => {
    setEditing(false);
    onUpdated(row.record_id, updates);
  };

  return (
    <>
      <tr
        className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors"
        data-testid={`flag-row-${row.record_id}`}
      >
        {/* Collection */}
        <td className="px-4 py-3 whitespace-nowrap">
          <CollectionBadge collection={row.collection} />
        </td>

        {/* Question */}
        <td className="px-4 py-3 text-sm text-slate-700 max-w-xs">
          <Truncated text={row.question_text} />
        </td>

        {/* Grade / Level */}
        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
          {isFM ? (row.level || "—") : (row.grade || "—")}
        </td>

        {/* Subject / Skill */}
        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
          {isFM ? (row.skill || "—") : (row.subject || "—")}
        </td>

        {/* Options — Subject: show A/B/C/D with correct highlighted; FM: — */}
        <td className="px-4 py-3">
          {isFM ? (
            <span className="text-slate-300 text-xs">n/a</span>
          ) : row.options ? (
            <div className="space-y-0.5">
              {["A", "B", "C", "D"].map((l) => {
                const isCorrect = row.correct_answer === l;
                return (
                  <div key={l} className={`text-xs flex gap-1.5 items-center ${isCorrect ? "font-bold text-emerald-700" : "text-slate-500"}`}>
                    <span className={`w-4 h-4 flex-shrink-0 rounded flex items-center justify-center text-xs ${isCorrect ? "bg-emerald-100" : ""}`}>{l}</span>
                    <span>{row.options[l] || "—"}</span>
                    {isCorrect && <Check className="w-3 h-3 text-emerald-600 flex-shrink-0" />}
                  </div>
                );
              })}
            </div>
          ) : (
            <span className="text-slate-300 text-xs">—</span>
          )}
        </td>

        {/* Correct answer */}
        <td className="px-4 py-3 text-sm font-semibold text-slate-700 whitespace-nowrap">
          {row.correct_answer || "—"}
        </td>

        {/* Flag reasons */}
        <td className="px-4 py-3">
          <div className="flex flex-wrap gap-1">
            {(row.flag_reasons || []).map((r) => (
              <ReasonBadge key={r} reason={r} />
            ))}
          </div>
        </td>

        {/* Flagged count */}
        <td className="px-4 py-3 text-center">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
            {row.flagged_count}
          </span>
        </td>

        {/* Last flagged */}
        <td className="px-4 py-3 whitespace-nowrap">
          <Ts iso={row.last_flagged} />
        </td>

        {/* Actions */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setEditing((e) => !e)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors"
              style={
                editing
                  ? { background: "#eff6ff", color: "#1d4ed8", borderColor: "#bfdbfe" }
                  : { background: "#f8fafc", color: "#475569", borderColor: "#e2e8f0" }
              }
              data-testid={`edit-btn-${row.record_id}`}
            >
              <Pencil className="w-3 h-3" />
              {editing ? "Close" : "Edit"}
            </button>
            <button
              type="button"
              onClick={() => onDismiss(row.record_id)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors"
              style={{ background: "#f8fafc", color: "#64748b", borderColor: "#e2e8f0" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#fef2f2";
                e.currentTarget.style.color = "#dc2626";
                e.currentTarget.style.borderColor = "#fca5a5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#f8fafc";
                e.currentTarget.style.color = "#64748b";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
              data-testid={`dismiss-btn-${row.record_id}`}
            >
              <X className="w-3 h-3" />
              Dismiss
            </button>
          </div>
        </td>
      </tr>

      {editing && (
        <tr>
          <td colSpan={10} className="p-0">
            <EditPanel
              row={row}
              token={token}
              onSaved={handleSaved}
              onCancel={() => setEditing(false)}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function QuestionFlags({ token }) {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState("all"); // "all" | "FM" | "Subject"

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getQuestionFlags(token);
      setFlags(data);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || "Failed to load flagged questions.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleDismiss = async (recordId) => {
    try {
      await dismissQuestionFlags(recordId, token);
      setFlags((prev) => prev.filter((f) => f.record_id !== recordId));
    } catch {
      // silent
    }
  };

  const handleUpdated = (recordId, updates) => {
    setFlags((prev) =>
      prev.map((f) =>
        f.record_id === recordId ? { ...f, ...updates } : f
      )
    );
  };

  const displayed =
    filter === "FM"
      ? flags.filter((f) => f.collection === "fm_questions")
      : filter === "Subject"
      ? flags.filter((f) => f.collection === "fuel_questions")
      : flags;

  const TABS = [
    { key: "all", label: "All" },
    { key: "FM", label: "FM" },
    { key: "Subject", label: "Subject" },
  ];

  return (
    <div className="p-6 lg:p-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div
          className="flex items-center justify-center w-9 h-9 rounded-xl flex-shrink-0"
          style={{ background: "#fef3c7" }}
        >
          <Flag className="w-4.5 h-4.5" style={{ color: "#d97706" }} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-800">Question Flags</h1>
          <p className="text-sm text-slate-500">Student-reported issues — open flags only</p>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1.5 mb-5" role="tablist">
        {TABS.map(({ key, label }) => {
          const count =
            key === "all"
              ? flags.length
              : key === "FM"
              ? flags.filter((f) => f.collection === "fm_questions").length
              : flags.filter((f) => f.collection === "fuel_questions").length;
          const active = filter === key;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={active}
              onClick={() => setFilter(key)}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-colors"
              style={
                active
                  ? { background: "#1e3a5f", color: "#fff" }
                  : { background: "#f1f5f9", color: "#64748b" }
              }
              data-testid={`filter-tab-${key}`}
            >
              {label}
              <span
                className="inline-flex items-center justify-center rounded-full text-xs px-1.5 py-0.5 min-w-[20px]"
                style={
                  active
                    ? { background: "rgba(255,255,255,0.2)", color: "#fff" }
                    : { background: "#e2e8f0", color: "#64748b" }
                }
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* States */}
      {loading && (
        <div className="flex items-center justify-center py-20 text-slate-400 gap-2" data-testid="flags-loading">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      )}

      {!loading && loadError && (
        <div className="flex items-center gap-2 text-red-600 text-sm py-8" data-testid="flags-error">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {loadError}
        </div>
      )}

      {!loading && !loadError && displayed.length === 0 && (
        <div
          className="text-center py-20 text-slate-400 text-sm"
          data-testid="flags-empty"
        >
          No flagged questions. All clear.
        </div>
      )}

      {/* Table */}
      {!loading && !loadError && displayed.length > 0 && (
        <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="flags-table">
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                  {[
                    "Collection",
                    "Question",
                    "Grade / Level",
                    "Subject / Skill",
                    "Options",
                    "Correct Answer",
                    "Flag Reason(s)",
                    "Flagged",
                    "Last Flagged",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayed.map((row) => (
                  <FlagRow
                    key={row.record_id}
                    row={row}
                    token={token}
                    onDismiss={handleDismiss}
                    onUpdated={handleUpdated}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
